<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { VPhoneInput } from 'v-phone-input'

import { isValidEmail } from '@/utils/validation'

import 'flag-icons/css/flag-icons.min.css'
import 'v-phone-input/styles'

// Mobile number + email, shared by the profile dialog and the profile page. Field rules check the
// format; requireEmail adds the required rule for the profile page, where clearing a stored e-mail
// must be blocked inline (the api rejects an empty one). The dialog gates emptiness itself, and the
// consumers own the padding.
const { mobileNumberOnly, hideIntro, requireEmail } = defineProps<{
  mobileNumberOnly?: boolean
  hideIntro?: boolean
  requireEmail?: boolean
}>()
const email = defineModel<string>('email', { required: true })
const mobileNumber = defineModel<string>('mobileNumber', { required: true })

const { t } = useI18n()

function emailRule(value: string) {
  return value === '' || isValidEmail(value) || t('common.invalid_email')
}

function requiredRule(value: string) {
  return value !== '' || t('common.required')
}
</script>

<template>
  <!-- The profile page heads the section itself and wants no intro sentence; the dialog keeps it. -->
  <div
    v-if="!hideIntro"
    class="text-body-medium pt-2 pb-6"
  >
    {{ t(mobileNumberOnly ? 'common.you_need_to_enter_your_phone_number' : 'common.how_may_we_be_in_touch_text') }}
  </div>

  <v-phone-input
    v-model="mobileNumber"
    :country-label="t('common.country')"
    :invalid-message="t('common.invalid_mobile_number')"
    :label="t('common.mobile_number')"
    :prefer-countries="['SE', 'DK', 'NO', 'FI', 'IS']"
    :rules="mobileNumberOnly ? [requiredRule] : []"
    country-icon-mode="svg"
    default-country="SE"
    variant="outlined"
    class="mb-4"
  />

  <v-text-field
    v-if="!mobileNumberOnly"
    v-model="email"
    :label="t('common.email')"
    :rules="requireEmail ? [requiredRule, emailRule] : [emailRule]"
    placeholder="example@example.com"
    type="email"
  />
</template>
